import { type AriaToastRegionProps, useToastRegion } from '@react-aria/toast';
import { type QueuedToast, useToastQueue } from '@react-stately/toast';
import { useRef, useState } from 'react';
import cx from 'clsx';
import { useHover } from 'react-aria';
import { AnimatePresence, motion } from 'motion/react';
import { getGlobalToastQueue } from './toast-queue';
import { ToastComponent, type ToastData } from './toast-component';
import { COLLAPSED_PEEK, EXPANDED_GAP, TOAST_TRANSITION, TOAST_VARIANTS } from './toast.animation';
import styles from './toast-context.module.css';

const DEFAULT_TOAST_HEIGHT = 60;
// Fixed base to ensure the exiting toast keeps its index when the stack shrinks
const STACK_Z_BASE = 10;

export function ToastRegion(props: AriaToastRegionProps) {
  const state = useToastQueue<ToastData>(getGlobalToastQueue());
  const ref = useRef(null);
  const { regionProps } = useToastRegion(props, state, ref);
  const [isExpanded, setIsExpanded] = useState(false);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [keepAlive, setKeepAlive] = useState(false);
  const { hoverProps } = useHover({
    onHoverStart: () => setIsExpanded(true),
    onHoverEnd: () => setIsExpanded(false),
  });

  // Stable ref callback per key; a fresh one each render would reattach the ref and force a reflow.
  const measureRefs = useRef(new Map<string, (node: HTMLDivElement | null) => void>());

  if (state.visibleToasts.length > 0 && !keepAlive) setKeepAlive(true);
  if (state.visibleToasts.length === 0 && !keepAlive) return null;

  function getMeasureRef(key: string): (node: HTMLDivElement | null) => void {
    let measureRef = measureRefs.current.get(key);
    if (!measureRef) {
      measureRef = (node: HTMLDivElement | null) => {
        if (!node) {
          measureRefs.current.delete(key);
          setHeights(prev => {
            if (!(key in prev)) return prev;
            const { [key]: _, ...rest } = prev;
            return rest;
          });
          return;
        }
        const height = node.offsetHeight;
        setHeights(prev => (prev[key] === height ? prev : { ...prev, [key]: height }));
      };
      measureRefs.current.set(key, measureRef);
    }
    return measureRef;
  }

  function heightOf(toast: QueuedToast<ToastData> | undefined): number {
    return (toast && heights[toast.key]) ?? DEFAULT_TOAST_HEIGHT;
  }

  function getExpandedY(index: number): number {
    let y = 0;
    for (let i = 0; i < index; i++) {
      y -= heightOf(state.visibleToasts[i]) + EXPANDED_GAP;
    }
    return y;
  }

  const collapsedHeight =
    heightOf(state.visibleToasts[0]) + (state.visibleToasts.length - 1) * COLLAPSED_PEEK;
  const expandedHeight =
    state.visibleToasts.reduce((sum, toast) => sum + heightOf(toast), 0) +
    (state.visibleToasts.length - 1) * EXPANDED_GAP;
  const regionHeight = isExpanded ? expandedHeight : collapsedHeight;

  return (
    <div
      {...regionProps}
      aria-live="polite"
      className={cx(styles.region, 'toast-region')}
      ref={ref}
      style={{ height: regionHeight }}
    >
      <AnimatePresence
        onExitComplete={() => {
          if (state.visibleToasts.length === 0) setKeepAlive(false);
        }}
      >
        {state.visibleToasts.map((toast, index) => (
          <motion.div
            animate={isExpanded ? 'expanded' : 'collapsed'}
            className={styles.toastWrapper}
            custom={{ index, expandedY: getExpandedY(index), isExpanded }}
            exit="exit"
            initial="initial"
            key={toast.key}
            ref={getMeasureRef(toast.key)}
            style={{ zIndex: STACK_Z_BASE - index }}
            transition={TOAST_TRANSITION}
            variants={TOAST_VARIANTS}
          >
            <div {...hoverProps}>
              <ToastComponent
                state={state}
                text={toast.content.text}
                toast={toast}
                type={toast.content.type}
              />
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
